// Seção "Negociação" do Registrar contato (specs/21). Só aparece com o módulo ligado.
import { hojeISO, moeda, num } from '../format';
import { COR_GANHO, COR_PERDIDO, fundoEtapa, type ConfigNegociacao, type NegociacaoCliente } from '../negociacao';

export interface EstadoNeg {
  /** 'NADA' | id da etapa | 'GANHO' | 'PERDIDO' */
  opcao: string; oportunidadeId: string; valor: string; quantidade: string; prazoEm: string; data: string; motivo: string;
  atualizarUltimaCompra: boolean;
}
export const estadoNegInicial = (): EstadoNeg => ({
  opcao: 'NADA', oportunidadeId: '', valor: '', quantidade: '', prazoEm: '', data: hojeISO(), motivo: '', atualizarUltimaCompra: true,
});
export const valorDeTexto = (v: string) => {
  const s = v.trim();
  if (!s) return 0;
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
};
const somaDias = (dias: number) => { const d = new Date(); d.setDate(d.getDate() + dias); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/** Erro de validação do lado do cliente (o servidor valida de novo). */
export function erroNeg(cfg: ConfigNegociacao, s: EstadoNeg): string | null {
  if (s.opcao === 'NADA') return null;
  const v = valorDeTexto(s.valor);
  if (s.opcao === 'GANHO') return !v ? 'Informe o valor vendido.' : !s.data ? 'Informe a data da venda.' : s.data > hojeISO() ? 'A data da venda não pode ser no futuro.' : null;
  if (s.opcao === 'PERDIDO') return s.motivo ? null : 'Escolha o motivo da perda.';
  const e = cfg.etapas.find((x) => x.id === s.opcao);
  if (!e) return null;
  if (e.pedeQuantidade && s.quantidade === '') return `Informe ${(e.rotuloQuantidade ?? 'a quantidade').toLowerCase()}.`;
  if (e.pedeValor === 'OBRIGATORIO' && !v) return `Informe ${(e.rotuloValor ?? 'o valor').toLowerCase()}.`;
  return null;
}

/** Corpo `negociacao` do POST /clientes/:id/contatos. */
export function payloadNeg(cfg: ConfigNegociacao, s: EstadoNeg) {
  if (s.opcao === 'NADA') return undefined;
  const v = valorDeTexto(s.valor);
  const base = { ...(s.oportunidadeId ? { oportunidadeId: s.oportunidadeId } : {}) };
  if (s.opcao === 'GANHO') return { ...base, destino: 'GANHO', valor: v, data: s.data, atualizarUltimaCompra: s.atualizarUltimaCompra };
  if (s.opcao === 'PERDIDO') return { ...base, destino: 'PERDIDO', motivo: s.motivo, ...(v ? { valor: v } : {}) };
  const e = cfg.etapas.find((x) => x.id === s.opcao)!;
  return {
    ...base, destino: 'ETAPA', etapaId: e.id,
    ...(e.pedeQuantidade ? { quantidade: Number(s.quantidade || 0) } : {}),
    ...(e.pedeValor !== 'NAO' && v ? { valor: v } : {}),
    ...(s.prazoEm ? { prazoEm: s.prazoEm } : {}),
  };
}

/** Estado ao escolher uma opção: já vincula a negociação aberta e traz valor e prazo dela. */
export function estadoPara(cfg: ConfigNegociacao, abertas: NegociacaoCliente[], opcao: string, oportunidadeId?: string): Partial<EstadoNeg> {
  const ab = (oportunidadeId ? abertas.find((o) => o.id === oportunidadeId) : undefined) ?? abertas[0];
  const e = cfg.etapas.find((x) => x.id === opcao);
  return {
    opcao, motivo: '', quantidade: '',
    oportunidadeId: opcao !== 'NADA' && ab ? ab.id : '',
    valor: ab && Number(ab.valor) && (opcao === 'GANHO' || opcao === 'PERDIDO' || (e && e.pedeValor !== 'NAO'))
      ? Number(ab.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '',
    prazoEm: e?.prazoDias ? somaDias(e.prazoDias) : '',
  };
}

export function SecaoNegociacao({ cfg, abertas, s, set, tentou, deOutros = [] }: {
  cfg: ConfigNegociacao; abertas: NegociacaoCliente[]; s: EstadoNeg; set: (p: Partial<EstadoNeg>) => void; tentou: boolean;
  /** Abertas de colegas neste cliente (specs/27): não aparecem para avançar, só o aviso */
  deOutros?: NegociacaoCliente[];
}) {
  const etapa = cfg.etapas.find((e) => e.id === s.opcao);
  const opcoes = [
    { id: 'NADA', nome: 'Só contato', cor: null as string | null },
    ...cfg.etapas.map((e) => ({ id: e.id, nome: e.nome, cor: e.cor })),
    { id: 'GANHO', nome: cfg.ganho?.nome ?? 'Vendeu', cor: COR_GANHO },
    { id: 'PERDIDO', nome: cfg.perdido?.nome ?? 'Perdeu', cor: COR_PERDIDO },
  ];
  // negociações que este passo pode avançar: abertas (amostra não avança a si mesma para outra amostra nova, mas pode atualizar)
  const podeVincular = s.opcao !== 'NADA' && abertas.length > 0;
  const atual = abertas.find((o) => o.id === s.oportunidadeId);
  const erro = tentou ? erroNeg(cfg, s) : null;
  const cor = s.opcao === 'GANHO' ? COR_GANHO : s.opcao === 'PERDIDO' ? COR_PERDIDO : etapa?.cor ?? null;

  const escolher = (id: string) => set(estadoPara(cfg, abertas, id));

  return (
    <div className="field"><span id="l-neg-contato">Negociação</span>
      <div className="opts-neg" role="group" aria-labelledby="l-neg-contato">
        {opcoes.map((o) => (
          <button key={o.id} type="button" className="opt-neg" aria-pressed={s.opcao === o.id} onClick={() => escolher(o.id)}
            style={s.opcao === o.id && o.cor ? { borderColor: o.cor, color: o.cor, background: fundoEtapa(o.cor) } : undefined}>{o.nome}</button>
        ))}
      </div>
      {s.opcao !== 'NADA' && deOutros.length > 0 && (
        <div className="aviso-transferencia" role="note">
          <strong>{[...new Set(deOutros.map((o) => o.responsavel ?? 'Outro vendedor'))].join(', ')} já {deOutros.length === 1 ? 'negocia' : 'negociam'} com este cliente</strong>
          <span>
            {deOutros.map((o) => `${o.etapa}${Number(o.valor) ? ` · ${moeda(o.valor)}` : ''}`).join('; ')}. {deOutros.length === 1 ? 'Essa negociação é do colega e não aparece' : 'Essas negociações são dos colegas e não aparecem'} aqui para avançar.
            Se for outro pedido, registre como nova negociação: ela conta para você.
          </span>
        </div>
      )}
      {s.opcao !== 'NADA' && (
        <div className="venda-box on" style={cor ? { borderColor: `${cor}66`, background: `${cor}14` } : undefined}>
          {podeVincular && (
            <label className="field"><span>Qual negociação</span>
              <select className="select" value={s.oportunidadeId} onChange={(e) => set({ oportunidadeId: e.target.value })}>
                {abertas.map((o) => (
                  <option key={o.id} value={o.id}>
                    {s.opcao === 'GANHO' ? 'Fechar como ganha: ' : s.opcao === 'PERDIDO' ? 'Fechar como perdida: ' : 'Avançar: '}
                    {o.etapa}{Number(o.valor) ? ` de ${moeda(o.valor)}` : o.quantidade != null ? ` (${num(o.quantidade)})` : ''}
                  </option>
                ))}
                <option value="">Nova negociação</option>
              </select>
            </label>
          )}
          <div className="venda-campos">
            {etapa?.pedeQuantidade && (
              <label className="field"><span>{etapa.rotuloQuantidade ?? 'Quantidade'} *</span>
                <input className="input num" inputMode="numeric" placeholder="0" value={s.quantidade} onChange={(e) => set({ quantidade: e.target.value.replace(/\D/g, '').slice(0, 9) })} />
              </label>
            )}
            {(s.opcao === 'GANHO' || s.opcao === 'PERDIDO' || (etapa && etapa.pedeValor !== 'NAO')) && (
              <label className="field"><span>{s.opcao === 'GANHO' ? 'Valor vendido *' : s.opcao === 'PERDIDO' ? 'Valor que deixou de vender' : `${etapa!.rotuloValor ?? 'Valor'}${etapa!.pedeValor === 'OBRIGATORIO' ? ' *' : ' (opcional)'}`}</span>
                <span className="input-prefixo"><span aria-hidden="true">R$</span>
                  <input className="input num" inputMode="decimal" placeholder="0,00" value={s.valor} onChange={(e) => set({ valor: e.target.value.replace(/[^\d.,]/g, '') })} />
                </span>
              </label>
            )}
            {etapa?.prazoDias && (
              <label className="field"><span>{etapa.pedeValor === 'NAO' ? 'Cobrar retorno em' : 'Válido até'}</span>
                <input className="input num" type="date" value={s.prazoEm} onChange={(e) => set({ prazoEm: e.target.value })} />
              </label>
            )}
            {s.opcao === 'GANHO' && (
              <label className="field"><span>Data da venda *</span>
                <input className="input num" type="date" max={hojeISO()} value={s.data} onChange={(e) => set({ data: e.target.value })} />
              </label>
            )}
          </div>
          {s.opcao === 'PERDIDO' && (
            <div className="field"><span>Motivo *</span>
              <div className="row" style={{ gap: 6 }}>
                {cfg.motivos.map((m) => <button key={m.id} type="button" className={`pill${s.motivo === m.nome ? ' on' : ''}`} aria-pressed={s.motivo === m.nome} onClick={() => set({ motivo: m.nome })}>{m.nome}</button>)}
              </div>
            </div>
          )}
          {s.opcao === 'GANHO' && (
            <label className="check" style={{ alignItems: 'flex-start' }}>
              <input type="checkbox" checked={s.atualizarUltimaCompra} onChange={(e) => set({ atualizarUltimaCompra: e.target.checked })} />
              <span>Atualizar a última compra do cliente<br /><span className="small faint">Usada no filtro “sem comprar há X dias” das campanhas.</span></span>
            </label>
          )}
          {erro && <span className="hint err">{erro}</span>}
          <span className="small faint">
            {s.opcao === 'GANHO' ? `Conta em vendas na data da venda${atual ? ` e fecha ${atual.etapa.toLowerCase()} aberto` : ''}.`
              : s.opcao === 'PERDIDO' ? 'Não entra em vendas; conta em “Perdido”.'
              : etapa?.pedeValor !== 'NAO' && etapa?.somaPipeline ? 'Conta em “R$ em aberto”, não em vendas.' : 'Não soma no pipeline nem em vendas.'}
          </span>
        </div>
      )}
    </div>
  );
}
